import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import {
    getDepartmentsURL,
    createDepartmentURL,
    updateDepartmentURL,
    deleteDepartmentURL,
    getRolesURL,
    createRoleURL,
    updateRoleURL,
    deleteRoleURL
} from '../../../../../utils/urls';

const DepartmentsRolesTab = () => {
    // Departments State
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [departmentDialogVisible, setDepartmentDialogVisible] = useState(false);
    const [isDepartmentEdit, setIsDepartmentEdit] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [departmentFormData, setDepartmentFormData] = useState({
        name: '',
        code: '',
        description: ''
    });

    // Roles State
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [roleDialogVisible, setRoleDialogVisible] = useState(false);
    const [isRoleEdit, setIsRoleEdit] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleFormData, setRoleFormData] = useState({
        name: '',
        code: '',
        description: '',
        level: 1,
        permissions: [],
        department: null
    });

    useEffect(() => {
        loadDepartments();
        loadRoles();
    }, []);

    // ==================== DEPARTMENTS FUNCTIONS ====================

    const loadDepartments = async () => {
        setDepartmentsLoading(true);
        try {
            const response = await makeRequest2(getDepartmentsURL, 'GET', {});

            if (response.success && response.data && response.data.success) {
                const departmentsData = response.data.data || [];
                setDepartments(departmentsData);
            } else if (response.success === false || (response.data && response.data.success === false)) {
                const errorData = response.data || response;
                toastify(errorData.error || errorData.message || 'Failed to load departments', 'error');
                setDepartments([]);
            } else {
                toastify('Failed to load departments', 'error');
                setDepartments([]);
            }
        } catch (error) {
            toastify(error.message || 'Error loading departments', 'error');
            setDepartments([]);
        }
        setDepartmentsLoading(false);
    };

    const handleAddDepartment = () => {
        setIsDepartmentEdit(false);
        setDepartmentFormData({
            name: '',
            code: '',
            description: ''
        });
        setSelectedDepartment(null);
        setDepartmentDialogVisible(true);
    };

    const handleEditDepartment = (department) => {
        setIsDepartmentEdit(true);
        setSelectedDepartment(department);
        setDepartmentFormData({
            name: department.name || '',
            code: department.code || '',
            description: department.description || ''
        });
        setDepartmentDialogVisible(true);
    };

    const handleSaveDepartment = async () => {
        if (!departmentFormData.name.trim()) {
            toastify('Department name is required', 'error');
            return;
        }

        if (!departmentFormData.code.trim()) {
            toastify('Department code is required', 'error');
            return;
        }

        try {
            const payload = {
                ...departmentFormData
            };

            let response;
            if (isDepartmentEdit) {
                const url = updateDepartmentURL.replace(':department_id', selectedDepartment._id || selectedDepartment.id);
                response = await makeRequest2(url, 'PUT', payload);
            } else {
                response = await makeRequest2(createDepartmentURL, 'POST', payload);
            }

            if (response.success && response.data) {
                if (response.data.success === true || response.data.success === undefined) {
                    toastify(response.data.message || (isDepartmentEdit ? 'Department updated successfully' : 'Department created successfully'), 'success');
                    setDepartmentDialogVisible(false);
                    loadDepartments();
                } else {
                    const errorMsg = response.data?.error || 'Failed to save department';
                    toastify(errorMsg, 'error');
                }
            } else {
                const errorMsg = response.error || 'Failed to save department';
                toastify(errorMsg, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to save department', 'error');
        }
    };

    const handleArchiveDepartment = (department) => {
        confirmDialog({
            message: `Are you sure you want to archive "${department.name}"? This department will be hidden from active lists but can be restored later.`,
            header: 'Confirm Archive',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-warning',
            accept: () => performArchiveDepartment(department)
        });
    };

    const performArchiveDepartment = async (department) => {
        try {
            const url = deleteDepartmentURL.replace(':department_id', department._id || department.id);
            const response = await makeRequest2(url, 'DELETE', {});

            if (response.success && response.data) {
                if (response.data.success === true || response.data.success === undefined) {
                    toastify(response.data.message || 'Department archived successfully', 'success');
                    loadDepartments();
                } else {
                    const errorMsg = response.data?.error || 'Failed to archive department';
                    confirmDialog({
                        message: errorMsg,
                        header: 'Cannot Archive Department',
                        icon: 'pi pi-info-circle',
                        acceptLabel: 'OK',
                        rejectClassName: 'hidden',
                        accept: () => {}
                    });
                }
            } else {
                const errorMsg = response.error || 'Failed to archive department';
                confirmDialog({
                    message: errorMsg,
                    header: 'Cannot Archive Department',
                    icon: 'pi pi-info-circle',
                    acceptLabel: 'OK',
                    rejectClassName: 'hidden',
                    accept: () => {}
                });
            }
        } catch (error) {
            const errorMsg = error.message || 'Failed to archive department';
            confirmDialog({
                message: errorMsg,
                header: 'Error',
                icon: 'pi pi-times-circle',
                acceptLabel: 'OK',
                rejectClassName: 'hidden',
                accept: () => {}
            });
        }
    };

    // ==================== ROLES FUNCTIONS ====================

    const loadRoles = async () => {
        setRolesLoading(true);
        try {
            const response = await makeRequest2(getRolesURL, 'GET', {});

            if (response.success && response.data && response.data.success) {
                const rolesData = response.data.data || [];
                setRoles(rolesData);
            } else if (response.success === false || (response.data && response.data.success === false)) {
                const errorData = response.data || response;
                toastify(errorData.error || errorData.message || 'Failed to load roles', 'error');
                setRoles([]);
            } else {
                toastify('Failed to load roles', 'error');
                setRoles([]);
            }
        } catch (error) {
            toastify(error.message || 'Error loading roles', 'error');
            setRoles([]);
        }
        setRolesLoading(false);
    };

    const handleAddRole = () => {
        setIsRoleEdit(false);
        setRoleFormData({
            name: '',
            code: '',
            description: '',
            level: 1,
            permissions: [],
            department: null
        });
        setSelectedRole(null);
        setRoleDialogVisible(true);
    };

    const handleEditRole = (role) => {
        setIsRoleEdit(true);
        setSelectedRole(role);
        setRoleFormData({
            name: role.name || '',
            code: role.code || '',
            description: role.description || '',
            level: role.level || 1,
            permissions: role.permissions || [],
            department: role.department?._id || role.department || null
        });
        setRoleDialogVisible(true);
    };

    const handleSaveRole = async () => {
        if (!roleFormData.name.trim()) {
            toastify('Role name is required', 'error');
            return;
        }

        if (!roleFormData.code.trim()) {
            toastify('Role code is required', 'error');
            return;
        }

        if (!roleFormData.level || roleFormData.level < 1 || roleFormData.level > 10) {
            toastify('Role level must be between 1 and 10', 'error');
            return;
        }

        try {
            const payload = {
                ...roleFormData
            };

            let response;
            if (isRoleEdit) {
                const url = updateRoleURL.replace(':role_id', selectedRole._id || selectedRole.id);
                response = await makeRequest2(url, 'PUT', payload);
            } else {
                response = await makeRequest2(createRoleURL, 'POST', payload);
            }

            if (response.success && response.data) {
                if (response.data.success === true || response.data.success === undefined) {
                    toastify(response.data.message || (isRoleEdit ? 'Role updated successfully' : 'Role created successfully'), 'success');
                    setRoleDialogVisible(false);
                    loadRoles();
                } else {
                    const errorMsg = response.data?.error || 'Failed to save role';
                    toastify(errorMsg, 'error');
                }
            } else {
                const errorMsg = response.error || 'Failed to save role';
                toastify(errorMsg, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to save role', 'error');
        }
    };

    const handleArchiveRole = (role) => {
        confirmDialog({
            message: `Are you sure you want to archive "${role.name}"? This role will be hidden from active lists but can be restored later.`,
            header: 'Confirm Archive',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-warning',
            accept: () => performArchiveRole(role)
        });
    };

    const performArchiveRole = async (role) => {
        try {
            const url = deleteRoleURL.replace(':role_id', role._id || role.id);
            const response = await makeRequest2(url, 'DELETE', {});

            if (response.success && response.data) {
                if (response.data.success === true || response.data.success === undefined) {
                    toastify(response.data.message || 'Role archived successfully', 'success');
                    loadRoles();
                } else {
                    const errorMsg = response.data?.error || 'Failed to archive role';
                    confirmDialog({
                        message: errorMsg,
                        header: 'Cannot Archive Role',
                        icon: 'pi pi-info-circle',
                        acceptLabel: 'OK',
                        rejectClassName: 'hidden',
                        accept: () => {}
                    });
                }
            } else {
                const errorMsg = response.error || 'Failed to archive role';
                confirmDialog({
                    message: errorMsg,
                    header: 'Cannot Archive Role',
                    icon: 'pi pi-info-circle',
                    acceptLabel: 'OK',
                    rejectClassName: 'hidden',
                    accept: () => {}
                });
            }
        } catch (error) {
            const errorMsg = error.message || 'Failed to archive role';
            confirmDialog({
                message: errorMsg,
                header: 'Error',
                icon: 'pi pi-times-circle',
                acceptLabel: 'OK',
                rejectClassName: 'hidden',
                accept: () => {}
            });
        }
    };

    // ==================== BODY TEMPLATES ====================

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.active !== false ? 'success' : 'secondary';
        const value = rowData.active !== false ? 'Active' : 'Archived';
        return <Tag value={value} severity={severity} />;
    };

    const departmentActionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEditDepartment(rowData)}
                    tooltip="Edit Department"
                />
                <Button
                    icon="pi pi-box"
                    className="p-button-rounded p-button-warning p-button-sm"
                    onClick={() => handleArchiveDepartment(rowData)}
                    tooltip="Archive Department"
                />
            </div>
        );
    };

    const roleActionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEditRole(rowData)}
                    tooltip="Edit Role"
                />
                <Button
                    icon="pi pi-box"
                    className="p-button-rounded p-button-warning p-button-sm"
                    onClick={() => handleArchiveRole(rowData)}
                    tooltip="Archive Role"
                />
            </div>
        );
    };

    const roleLevelBodyTemplate = (rowData) => {
        return <Tag value={`Level ${rowData.level}`} severity="info" />;
    };

    const departmentBodyTemplate = (rowData) => {
        if (rowData.department) {
            return <Tag value={rowData.department.name} severity="success" />;
        }
        return <Tag value="Unassigned" severity="secondary" />;
    };

    // ==================== DIALOG FOOTERS ====================

    const departmentDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={() => setDepartmentDialogVisible(false)} className="p-button-text" />
            <Button label="Save" icon="pi pi-check" onClick={handleSaveDepartment} />
        </div>
    );

    const roleDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={() => setRoleDialogVisible(false)} className="p-button-text" />
            <Button label="Save" icon="pi pi-check" onClick={handleSaveRole} />
        </div>
    );

    return (
        <>
            {/* ==================== USAGE GUIDELINES ==================== */}
            <div className="row mb-3">
                <div className="col-sm-12">
                    <div className="alert alert-info">
                        <h6 className="alert-heading"><i className="ti ti-info-circle"></i> Important Guidelines</h6>
                        <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                            <li><strong>Department Codes:</strong> Use UPPERCASE (e.g., CS, TECH, SALES). These match agent assignments.</li>
                            <li><strong>Role Codes:</strong> Use lowercase_with_underscores (e.g., agent, team_leader, supervisor). These are used for agent login and permissions.</li>
                            <li><strong>Create departments and roles BEFORE adding agents</strong> - agents must reference existing roles.</li>
                            <li><strong>Cannot archive if in use:</strong> Departments/roles with active agents cannot be archived.</li>
                            <li><strong>Role Level:</strong> Higher levels = more authority (1=lowest, 10=highest). Used for permission hierarchy.</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* ==================== DEPARTMENTS SECTION ==================== */}
            <div className="row">
                <div className="col-sm-6 mb-4">
                    <div className="card">
                        <div className="card-header">
                            <div className="row">
                                <div className="col-8">
                                    <h5>Departments</h5>
                                </div>
                                <div className="col-4">
                                    <button
                                        className="btn btn-primary float-end"
                                        style={{ color: 'white' }}
                                        onClick={handleAddDepartment}
                                    >
                                        <i className="ti ti-plus" />
                                        &nbsp;Add New Department
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={departments}
                                    emptyMessage="No departments found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    loading={departmentsLoading}
                                >
                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        style={{ width: '35%' }}
                                    />
                                    <Column
                                        field="code"
                                        header="Code"
                                        sortable
                                        style={{ width: '25%' }}
                                    />
                                    <Column
                                        header="Status"
                                        body={statusBodyTemplate}
                                        sortable
                                        style={{ width: '20%' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={departmentActionBodyTemplate}
                                        exportable={false}
                                        style={{ width: '20%' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ==================== ROLES SECTION ==================== */}
                <div className="col-sm-6 mb-4">
                    <div className="card">
                        <div className="card-header">
                            <div className="row">
                                <div className="col-8">
                                    <h5>Roles</h5>
                                </div>
                                <div className="col-4">
                                    <button
                                        className="btn btn-primary float-end"
                                        style={{ color: 'white' }}
                                        onClick={handleAddRole}
                                    >
                                        <i className="ti ti-plus" />
                                        &nbsp;Add New Role
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={roles}
                                    emptyMessage="No roles found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    loading={rolesLoading}
                                >
                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        style={{ width: '30%' }}
                                    />
                                    <Column
                                        field="code"
                                        header="Code"
                                        sortable
                                        style={{ width: '25%' }}
                                    />
                                    <Column
                                        header="Level"
                                        body={roleLevelBodyTemplate}
                                        sortable
                                        style={{ width: '15%' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={roleActionBodyTemplate}
                                        exportable={false}
                                        style={{ width: '30%' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== DEPARTMENT DIALOG ==================== */}
            <Dialog
                visible={departmentDialogVisible}
                style={{ width: '50vw' }}
                header={isDepartmentEdit ? 'Edit Department' : 'Add New Department'}
                modal
                footer={departmentDialogFooter}
                onHide={() => setDepartmentDialogVisible(false)}
            >
                <div className="p-fluid">
                    <div className="field mb-3">
                        <label htmlFor="dept-name">Department Name *</label>
                        <Dropdown
                            id="dept-name"
                            value={departmentFormData.name}
                            options={[
                                { label: 'Customer Support', value: 'Customer Support' },
                                { label: 'Technical Services', value: 'Technical Services' },
                                { label: 'Sales & Marketing', value: 'Sales & Marketing' },
                                { label: 'Quality Assurance', value: 'Quality Assurance' },
                                { label: 'Field Operations', value: 'Field Operations' },
                                { label: 'Custom...', value: 'custom' }
                            ]}
                            onChange={(e) => {
                                if (e.value === 'custom') {
                                    setDepartmentFormData({ ...departmentFormData, name: '' });
                                } else {
                                    setDepartmentFormData({ ...departmentFormData, name: e.value });
                                }
                            }}
                            placeholder="Select or type custom name"
                            editable
                        />
                        <small className="text-muted">Select from suggestions or type your own</small>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="dept-code">Department Code *</label>
                        <Dropdown
                            id="dept-code"
                            value={departmentFormData.code}
                            options={[
                                { label: 'CS - Customer Support', value: 'CS' },
                                { label: 'TECH - Technical', value: 'TECH' },
                                { label: 'SALES - Sales', value: 'SALES' },
                                { label: 'QA - Quality Assurance', value: 'QA' },
                                { label: 'FIELD - Field Ops', value: 'FIELD' },
                                { label: 'Custom...', value: 'custom' }
                            ]}
                            onChange={(e) => {
                                if (e.value === 'custom') {
                                    setDepartmentFormData({ ...departmentFormData, code: '' });
                                } else {
                                    setDepartmentFormData({ ...departmentFormData, code: e.value.toUpperCase() });
                                }
                            }}
                            placeholder="Select or type custom code"
                            editable
                        />
                        <small className="text-muted">UPPERCASE codes (e.g., CS, TECH, SALES) - Select or type your own</small>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="dept-description">Description</label>
                        <InputTextarea
                            id="dept-description"
                            value={departmentFormData.description}
                            onChange={(e) => setDepartmentFormData({ ...departmentFormData, description: e.target.value })}
                            rows={3}
                        />
                    </div>
                </div>
            </Dialog>

            {/* ==================== ROLE DIALOG ==================== */}
            <Dialog
                visible={roleDialogVisible}
                style={{ width: '50vw' }}
                header={isRoleEdit ? 'Edit Role' : 'Add New Role'}
                modal
                footer={roleDialogFooter}
                onHide={() => setRoleDialogVisible(false)}
            >
                <div className="p-fluid">
                    <div className="field mb-3">
                        <label htmlFor="role-name">Role Name *</label>
                        <Dropdown
                            id="role-name"
                            value={roleFormData.name}
                            options={[
                                { label: 'Call Center Agent', value: 'Call Center Agent' },
                                { label: 'Agent', value: 'Agent' },
                                { label: 'Technical Support Agent', value: 'Technical Support Agent' },
                                { label: 'Team Leader', value: 'Team Leader' },
                                { label: 'Supervisor', value: 'Supervisor' },
                                { label: 'Manager', value: 'Manager' },
                                { label: 'Custom...', value: 'custom' }
                            ]}
                            onChange={(e) => {
                                if (e.value === 'custom') {
                                    setRoleFormData({ ...roleFormData, name: '' });
                                } else {
                                    setRoleFormData({ ...roleFormData, name: e.value });
                                }
                            }}
                            placeholder="Select or type custom name"
                            editable
                        />
                        <small className="text-muted">Select from key roles or type your own</small>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="role-code">Role Code * (for Customer Obsession Portal)</label>
                        <Dropdown
                            id="role-code"
                            value={roleFormData.code}
                            options={[
                                { label: 'call_center_agent - Basic agent (Level 1-2)', value: 'call_center_agent' },
                                { label: 'agent - Regular agent (Level 2-3)', value: 'agent' },
                                { label: 'technician - Technical support (Level 3)', value: 'technician' },
                                { label: 'team_leader - Team lead (Level 5-6)', value: 'team_leader' },
                                { label: 'supervisor - Supervisor (Level 7-8)', value: 'supervisor' },
                                { label: 'manager - Manager (Level 9-10)', value: 'manager' },
                                { label: 'Custom...', value: 'custom' }
                            ]}
                            onChange={(e) => {
                                if (e.value === 'custom') {
                                    setRoleFormData({ ...roleFormData, code: '' });
                                } else {
                                    // Auto-fill level based on role code
                                    const roleLevelMap = {
                                        'call_center_agent': 1,
                                        'agent': 2,
                                        'technician': 3,
                                        'team_leader': 5,
                                        'team_lead': 5,
                                        'supervisor': 7,
                                        'manager': 9
                                    };
                                    const suggestedLevel = roleLevelMap[e.value.toLowerCase()] || roleFormData.level;
                                    setRoleFormData({
                                        ...roleFormData,
                                        code: e.value.toLowerCase(),
                                        level: suggestedLevel
                                    });
                                }
                            }}
                            placeholder="Select key role or type custom code"
                            editable
                        />
                        <small className="text-muted"><strong>Key roles control portal features:</strong> call_center_agent/agent (basic), team_leader (team stats), supervisor (reports), manager (full access)</small>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="role-description">Description</label>
                        <InputTextarea
                            id="role-description"
                            value={roleFormData.description}
                            onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                            rows={3}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="role-department">Department</label>
                        <Dropdown
                            id="role-department"
                            value={roleFormData.department}
                            options={departments.map(dept => ({ label: dept.name, value: dept._id || dept.id }))}
                            onChange={(e) => setRoleFormData({ ...roleFormData, department: e.value })}
                            placeholder="Select Department (Optional)"
                            showClear
                        />
                        <small className="text-muted">Assign this role to a specific department</small>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="role-level">Role Level * (1-10)</label>
                        <InputNumber
                            id="role-level"
                            value={roleFormData.level}
                            onValueChange={(e) => setRoleFormData({ ...roleFormData, level: e.value })}
                            min={1}
                            max={10}
                            showButtons
                        />
                        <small className="text-muted">Hierarchy level: 1 = lowest, 10 = highest</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default DepartmentsRolesTab;
